import React from 'react';
import { Youtube, Facebook, Instagram, Linkedin, MoreHorizontal, Edit2, Trash2, Send, Clock, Image, Video, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import StatusBadge from './StatusBadge';
import { PLATFORM_COLORS, truncateContent, formatScheduledDate } from '../../../utils/socialMediaHelpers';

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const PostCard = ({
    post,
    integration,
    onClick,
    onEdit,
    onDelete,
    onPublish,
    compact = false
}) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const menuRef = React.useRef(null);

    const platform = integration?.platform || post.platform;
    const Icon = PLATFORM_ICONS[platform] || Youtube;
    const colors = PLATFORM_COLORS[platform];

    const hasMedia = post.media_urls && post.media_urls.length > 0;
    const mediaType = hasMedia && post.media_urls[0]?.includes('video') ? 'video' : 'image';
    const canPublish = post.status === 'draft' || post.status === 'scheduled';
    const canEdit = post.status !== 'published' && post.status !== 'publishing';

    // Close menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (compact) {
        // Compact view for calendar cells
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={onClick}
                className={`group p-2 rounded-lg cursor-pointer transition-all border ${colors?.border || 'border-slate-200'} ${colors?.dark?.border || 'dark:border-slate-700'} ${colors?.bg || 'bg-slate-50'} ${colors?.dark?.bg || 'dark:bg-slate-800/50'} hover:shadow-md`}
            >
                <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-400'}`} />
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate flex-1">
                        {integration?.account_name || platform}
                    </span>
                    {post.status === 'failed' && (
                        <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                    )}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                    {truncateContent(post.content, 60)}
                </p>
            </motion.div>
        );
    }

    // Full view for post list
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-shadow"
        >
            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        {/* Platform icon with account avatar */}
                        <div className="relative">
                            {integration?.avatar_url ? (
                                <img
                                    src={integration.avatar_url}
                                    alt={integration.account_name}
                                    className="w-10 h-10 rounded-full border-2 border-white dark:border-slate-700 shadow"
                                />
                            ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors?.bg || 'bg-slate-100'} ${colors?.dark?.bg || 'dark:bg-slate-700'}`}>
                                    <Icon className={`w-5 h-5 ${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-400'}`} />
                                </div>
                            )}
                            <div className={`absolute -bottom-1 -right-1 p-1 rounded-full ${colors?.bg || 'bg-slate-100'} ${colors?.dark?.bg || 'dark:bg-slate-700'} border border-white dark:border-slate-700`}>
                                <Icon className={`w-3 h-3 ${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-400'}`} />
                            </div>
                        </div>
                        <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                                {integration?.display_name || integration?.account_name || platform}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                <Clock className="w-3.5 h-3.5" />
                                <span>
                                    {post.scheduled_at
                                        ? formatScheduledDate(post.scheduled_at)
                                        : post.published_at
                                            ? formatScheduledDate(post.published_at)
                                            : 'Not scheduled'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <StatusBadge status={post.status} />

                        {/* Actions menu */}
                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(!showMenu);
                                }}
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <MoreHorizontal className="w-5 h-5" />
                            </button>

                            {showMenu && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="absolute right-0 top-full mt-1 w-48 py-1 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-10"
                                >
                                    {canEdit && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowMenu(false);
                                                onEdit?.(post);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                            Edit
                                        </button>
                                    )}
                                    {canPublish && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowMenu(false);
                                                onPublish?.(post);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <Send className="w-4 h-4" />
                                            Publish Now
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowMenu(false);
                                            onDelete?.(post);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div onClick={onClick} className="cursor-pointer">
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap mb-3">
                        {truncateContent(post.content, 280)}
                    </p>

                    {/* Media preview */}
                    {hasMedia && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                            {mediaType === 'video' ? (
                                <Video className="w-4 h-4 text-slate-500" />
                            ) : (
                                <Image className="w-4 h-4 text-slate-500" />
                            )}
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                {post.media_urls.length} {mediaType}{post.media_urls.length !== 1 ? 's' : ''} attached
                            </span>
                        </div>
                    )}
                </div>

                {/* Error message for failed posts */}
                {post.status === 'failed' && post.error_message && (
                    <div className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700 dark:text-red-400">
                                {post.error_message}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default PostCard;
